<script setup>
import { computed, reactive, ref, shallowRef, watch } from 'vue';
import UiDialog from '@/components/ui/UiDialog.vue';
import UiFormField from '@/components/ui/UiFormField.vue';
import { apiRequest, assertOk, errorMessage } from '@/api/http';
import { useToast } from '@/composables/useToast';
import { required } from '@/utils/validation';

// Add or edit a room/section for a grade, with its adviser (and strand for
// Senior High).
const props = defineProps({
  room: { type: Object, default: null }, // null = add
  grade: { type: String, required: true },
  strands: { type: Array, default: () => [] },
});
const open = defineModel({ type: Boolean, default: false });
const emit = defineEmits(['saved']);

const toast = useToast();
const formRef = ref(null);
const saving = shallowRef(false);
const advisers = shallowRef([]);
const form = reactive({ name: '', adviser: null, strandId: null });

const isSenior = computed(() => ['Grade 11', 'Grade 12'].includes(props.grade));

// Teachers with the Adviser designation (Accounts) who don't advise a
// section yet. Choosing one makes them this section's adviser. When editing,
// the room's current adviser is kept in the list (it's "assigned", so the
// API leaves it out) — previously saving an edit silently removed the adviser.
async function loadAdvisers() {
  let rows = [];
  try {
    rows =
      (await apiRequest(
        'get',
        `/user-details/getAdvisoryNotAssigned/${encodeURIComponent(props.grade)}`,
      )) || [];
  } catch {
    rows = [];
  }
  const list = [...rows];
  const current = props.room;
  if (current?.teacherId && !list.some((t) => String(t.id) === String(current.teacherId))) {
    list.unshift({ id: current.teacherId, name: current.name });
  }
  advisers.value = list;
}

watch(open, (isOpen) => {
  if (!isOpen) return;
  form.name = props.room?.room_section ?? '';
  form.adviser = props.room?.teacherId ?? null;
  form.strandId = props.room?.strandId != null ? Number(props.room.strandId) : null;
  formRef.value?.resetValidation();
  loadAdvisers();
});

async function save() {
  const { valid } = await formRef.value.validate();
  if (!valid) return;
  saving.value = true;
  try {
    const data = {
      room_section: form.name.trim(),
      grade_level: props.grade,
      teacherId: form.adviser,
      ...(isSenior.value ? { strandId: form.strandId } : {}),
    };
    const body = props.room
      ? await apiRequest('patch', `/rooms-section/${props.room.id}`, data)
      : await apiRequest('post', '/rooms-section', data);
    assertOk(body);
    toast.success(props.room ? 'Room updated.' : 'Room added.');
    open.value = false;
    emit('saved');
  } catch (e) {
    toast.error(errorMessage(e, 'Could not save the room.'));
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <UiDialog
    v-model="open"
    :title="room ? 'Edit room' : 'Add room'"
    :description="grade"
    size="md"
  >
    <v-form ref="formRef" class="form-stack" @submit.prevent="save">
      <UiFormField label="Room name" required v-slot="{ id }">
        <v-text-field
          :id="id"
          v-model="form.name"
          placeholder="e.g. Rizal"
          :rules="[required]"
          hide-details="auto"
        />
      </UiFormField>
      <UiFormField
        label="Class adviser"
        description="Teachers with the Adviser designation who don't advise another section yet."
        v-slot="{ id }"
      >
        <v-autocomplete
          :id="id"
          v-model="form.adviser"
          :items="advisers"
          item-title="name"
          item-value="id"
          placeholder="Select an adviser"
          no-data-text="No free advisers. Give a teacher the Adviser designation in Accounts first."
          clearable
          hide-details="auto"
        />
      </UiFormField>
      <UiFormField v-if="isSenior" label="Strand" required v-slot="{ id }">
        <v-autocomplete
          :id="id"
          v-model="form.strandId"
          :items="strands"
          item-title="strand_name"
          item-value="id"
          placeholder="Select a strand"
          :rules="[required]"
          hide-details="auto"
        />
      </UiFormField>
    </v-form>

    <template #footer="{ close }">
      <v-btn variant="outlined" @click="close">Cancel</v-btn>
      <v-btn color="primary" :loading="saving" @click="save">
        {{ room ? 'Save changes' : 'Add room' }}
      </v-btn>
    </template>
  </UiDialog>
</template>
